/* Warranty Registration — validation + submission (no dependencies) */
(() => {
  'use strict';

  const pad = (n) => String(n).padStart(2, '0');
  const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  const uid = () =>
    window.crypto && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

  // Accepts "+91 98765 43210", "098765 43210", "9876543210" → "9876543210"
  const normalizePhone = (v) => {
    let d = String(v).replace(/\D/g, '');
    if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
    else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
    return d.slice(0, 10);
  };

  const NAME_RE = /^[\p{L}\p{M}][\p{L}\p{M} .'’-]*$/u;
  const PHONE_RE = /^[6-9]\d{9}$/;
  const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;
  const INVOICE_RE = /^[A-Za-z0-9][A-Za-z0-9\/\-_.# ]{1,39}$/;

  const RULES = {
    full_name: (v) =>
      !v ? 'Enter your full name.'
      : v.length < 2 ? 'Name must be at least 2 characters.'
      : !NAME_RE.test(v) ? 'Use letters only — spaces, dots, hyphens and apostrophes are fine.'
      : '',
    phone: (v) =>
      !v ? 'Enter your mobile number.'
      : !PHONE_RE.test(v) ? 'Enter a 10-digit Indian mobile number starting with 6, 7, 8 or 9.'
      : '',
    email: (v) =>
      !v ? 'Enter your email address.'
      : !EMAIL_RE.test(v) ? 'Enter a valid email address, like name@example.com.'
      : '',
    address: (v) =>
      !v ? 'Enter your full address.'
      : v.length < 10 ? 'Include house/flat no., street, city and PIN code.'
      : '',
    purchase_from: (v) => (!v ? 'Choose where you bought the product.' : ''),
    invoice_number: (v) =>
      !v ? 'Enter your invoice number.'
      : !INVOICE_RE.test(v) ? 'Use 2–40 letters, numbers or / - _ . # characters.'
      : '',
    invoice_date: (v) =>
      !v ? 'Select your invoice date.'
      : !/^\d{4}-\d{2}-\d{2}$/.test(v) || v < '2000-01-01' ? 'Select a valid invoice date.'
      : v > todayISO() ? 'Invoice date can’t be in the future.'
      : '',
  };

  const GENERIC_ERROR = 'Registration couldn’t be submitted. Check your connection and try again.';

  class WarrantyRegistration {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('form');
      if (!this.form) return;

      this.fieldsWrap = root.querySelector('[data-wr-fields]');
      this.success = root.querySelector('[data-wr-success]');
      this.status = root.querySelector('[data-wr-status]');
      this.submitBtn = root.querySelector('[data-wr-submit]');
      this.honeypot = root.querySelector('[data-wr-hp]');
      this.endpoint = (root.dataset.endpoint || '').trim();
      this.mode = root.dataset.mode === 'sheets' && this.endpoint ? 'sheets' : 'native';

      this.inputs = {};
      root.querySelectorAll('[data-wr-input]').forEach((el) => {
        this.inputs[el.dataset.wrInput] = el;
      });

      this.touched = new Set();
      this.busy = false;
      this.startedAt = Date.now();
      this.submissionId = uid(); // stays the same across retries → server de-duplicates

      this.bind();
    }

    bind() {
      const { phone, invoice_date: date } = this.inputs;
      if (date) date.max = todayISO();

      if (phone) {
        phone.addEventListener('input', () => {
          const n = normalizePhone(phone.value);
          if (n !== phone.value) phone.value = n;
        });
      }

      Object.entries(this.inputs).forEach(([name, el]) => {
        el.addEventListener('blur', () => {
          if (el.value.trim() || this.touched.has(name)) {
            this.touched.add(name);
            this.validateField(name);
          }
        });
        el.addEventListener(el.tagName === 'SELECT' ? 'change' : 'input', () => {
          if (this.touched.has(name)) this.validateField(name);
        });
      });

      this.form.addEventListener('submit', (e) => this.onSubmit(e));

      const copy = this.root.querySelector('[data-wr-copy]');
      if (copy) copy.addEventListener('click', () => this.copyRef(copy));

      // Re-enable the button if the page is restored from back/forward cache
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) this.setBusy(false);
      });
    }

    value(name) {
      const el = this.inputs[name];
      if (!el) return '';
      let v = el.value.trim();
      if (name === 'full_name') v = v.replace(/\s+/g, ' ');
      if (name === 'email') v = v.toLowerCase();
      if (name === 'phone') v = normalizePhone(v);
      return v;
    }

    validateField(name) {
      const rule = RULES[name];
      if (!rule || !this.inputs[name]) return true;
      const msg = rule(this.value(name));
      this.setFieldError(name, msg);
      return !msg;
    }

    validateAll() {
      return Object.keys(RULES).filter((name) => {
        if (!this.inputs[name]) return false;
        this.touched.add(name);
        return !this.validateField(name);
      });
    }

    setFieldError(name, msg) {
      const el = this.inputs[name];
      const field = el.closest('.wr__field');
      const err = field && field.querySelector('.wr__error');
      if (field) field.classList.toggle('is-invalid', Boolean(msg));
      if (msg) el.setAttribute('aria-invalid', 'true');
      else el.removeAttribute('aria-invalid');
      if (err) err.textContent = msg || '';
    }

    onSubmit(e) {
      if (this.busy) {
        e.preventDefault();
        return;
      }
      this.clearStatus();

      const invalid = this.validateAll();
      if (invalid.length) {
        e.preventDefault();
        this.showStatus(
          invalid.length === 1 ? 'Fix the highlighted field to continue.' : `Fix the ${invalid.length} highlighted fields to continue.`,
          'error'
        );
        this.inputs[invalid[0]].focus();
        return;
      }

      if (this.honeypot && this.honeypot.value) {
        e.preventDefault();
        return;
      }

      if (this.mode === 'native') {
        // Let Shopify's contact form submit normally; just lock the button.
        const phone = this.inputs.phone;
        if (phone) phone.value = this.value('phone');
        this.setBusy(true);
        return;
      }

      e.preventDefault();
      this.send();
    }

    async send() {
      this.setBusy(true);
      this.showStatus('Submitting your registration…', 'info');

      const payload = {
        submission_id: this.submissionId,
        full_name: this.value('full_name'),
        phone: this.value('phone'),
        email: this.value('email'),
        purchase_from: this.value('purchase_from'),
        invoice_number: this.value('invoice_number'),
        invoice_date: this.value('invoice_date'),
        address: this.value('address'),
        page_url: location.origin + location.pathname,
        elapsed_ms: Date.now() - this.startedAt,
        website: this.honeypot ? this.honeypot.value : '',
      };

      const controller = 'AbortController' in window ? new AbortController() : null;
      const timer = controller ? setTimeout(() => controller.abort(), 25000) : null;

      try {
        // text/plain avoids a CORS preflight, which Apps Script can't answer.
        const res = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(payload),
          signal: controller ? controller.signal : undefined,
        });

        let data;
        try {
          data = await res.json();
        } catch (_) {
          throw new Error('bad_response');
        }

        if (data.ok) {
          this.showSuccess(data);
          return;
        }

        if (data.errors && typeof data.errors === 'object') {
          const names = Object.keys(data.errors).filter((n) => this.inputs[n]);
          names.forEach((n) => this.setFieldError(n, data.errors[n]));
          this.showStatus(data.message || 'Fix the highlighted fields to continue.', 'error');
          if (names[0]) this.inputs[names[0]].focus();
          return;
        }

        this.showStatus(data.message || GENERIC_ERROR, 'error');
      } catch (err) {
        this.showStatus(
          err && err.name === 'AbortError'
            ? 'The request timed out. Check your connection and submit again — you won’t be registered twice.'
            : GENERIC_ERROR,
          'error'
        );
      } finally {
        if (timer) clearTimeout(timer);
        this.setBusy(false);
      }
    }

    showSuccess(data) {
      this.clearStatus();
      if (this.fieldsWrap) this.fieldsWrap.hidden = true;
      if (!this.success) return;

      const refBox = this.success.querySelector('[data-wr-ref]');
      const refVal = this.success.querySelector('[data-wr-ref-value]');
      const emailNote = this.success.querySelector('[data-wr-email-note]');
      if (data.reference && refBox && refVal) {
        refVal.textContent = data.reference;
        refBox.hidden = false;
      }
      if (emailNote) emailNote.hidden = !data.emailed;

      this.success.hidden = false;

      const top = this.root.getBoundingClientRect().top + window.scrollY - 120;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });

      const title = this.success.querySelector('[data-wr-success-title]');
      if (title) title.focus({ preventScroll: true });
    }

    async copyRef(btn) {
      const val = this.root.querySelector('[data-wr-ref-value]');
      if (!val || !navigator.clipboard) return;
      try {
        await navigator.clipboard.writeText(val.textContent.trim());
        btn.textContent = 'Copied';
        setTimeout(() => (btn.textContent = 'Copy'), 2000);
      } catch (_) {
        /* clipboard blocked — the number is still visible */
      }
    }

    setBusy(state) {
      this.busy = state;
      if (!this.submitBtn) return;
      this.submitBtn.disabled = state;
      this.submitBtn.setAttribute('aria-busy', String(state));
      this.form.setAttribute('aria-busy', String(state));
    }

    showStatus(msg, type) {
      if (!this.status) return;
      this.status.textContent = msg;
      this.status.dataset.type = type;
      this.status.hidden = false;
    }

    clearStatus() {
      if (!this.status) return;
      this.status.textContent = '';
      this.status.hidden = true;
      delete this.status.dataset.type;
    }
  }

  const init = (scope) => {
    (scope || document).querySelectorAll('[data-wr-root]').forEach((root) => {
      if (!root.__wr) root.__wr = new WarrantyRegistration(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
